import { useEffect, useMemo, useState } from 'react';
import {
  fetchMe,
  fetchRoster,
  fetchProfileUrl,
  ApiError,
  type MeResponse,
  type RosterMember,
  type RosterResponse,
} from '@/launch/api';

interface Props {
  token: string;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'no_namedrop'; me: MeResponse }
  | { status: 'ready'; me: MeResponse; roster: RosterResponse };

export function RosterView({ token }: Props): JSX.Element {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchMe(token);
        if (cancelled) return;
        if (!me.nameDropEnabled) {
          setState({ status: 'no_namedrop', me });
          return;
        }
        const roster = await fetchRoster(token);
        if (cancelled) return;
        setState({ status: 'ready', me, roster });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? `${err.code}: ${err.message}`
            : err instanceof Error
              ? err.message
              : String(err);
        setState({ status: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.status === 'loading') {
    return <CenteredCard>Loading…</CenteredCard>;
  }
  if (state.status === 'error') {
    return (
      <CenteredCard>
        <h1 className="text-lg font-semibold text-gray-900">Couldn't load roster</h1>
        <p className="mt-2 text-sm text-red-600 break-words">{state.message}</p>
      </CenteredCard>
    );
  }
  if (state.status === 'no_namedrop') {
    return (
      <CenteredCard>
        <h1 className="text-lg font-semibold text-gray-900">
          Welcome {state.me.name ?? state.me.email ?? 'learner'}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          NameDrop pronunciation features aren't configured for{' '}
          <strong>{state.me.tenantDisplayName}</strong> yet. Ask your administrator to set the
          tenant's NameDrop org slug.
        </p>
      </CenteredCard>
    );
  }

  return <Roster token={token} me={state.me} roster={state.roster} />;
}

function CenteredCard({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md w-full rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        {children}
      </div>
    </main>
  );
}

interface RosterProps {
  token: string;
  me: MeResponse;
  roster: RosterResponse;
}

function Roster({ token, me, roster }: RosterProps): JSX.Element {
  const [filter, setFilter] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileUrl, setProfileUrl] = useState<string | null>(null);

  const otherMembers = useMemo<RosterMember[]>(() => {
    const lowerEmail = me.email?.toLowerCase();
    const all = roster.members.filter((m) => m.email.toLowerCase() !== lowerEmail);
    if (!filter.trim()) return all;
    const q = filter.trim().toLowerCase();
    return all.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        (m.pronouns ?? '').toLowerCase().includes(q),
    );
  }, [roster.members, me.email, filter]);

  async function onEditProfile(): Promise<void> {
    setProfileError(null);
    setProfileBusy(true);
    try {
      const { profileUrl: url } = await fetchProfileUrl(token);
      setProfileUrl(url);
    } catch (err) {
      setProfileError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setProfileBusy(false);
    }
  }

  const isAdmin = me.role.toLowerCase().includes('admin') ||
    me.role.toLowerCase().includes('instructor') ||
    me.role.toLowerCase().includes('teacher');

  return (
    <main className="min-h-screen bg-gradient-to-b from-orange-50/40 to-gray-50 p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <HeroCard
          me={me}
          currentUser={roster.currentUser}
          onEditProfile={onEditProfile}
          busy={profileBusy}
          error={profileError}
        />

        {!roster.courseScoped && isAdmin ? (
          <p className="text-xs text-gray-500 px-1">
            This tenant doesn't have course-scoped rosters yet — showing everyone in the
            organization.
          </p>
        ) : null}

        <div className="flex items-center gap-3">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name, email, or pronouns…"
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {otherMembers.length} of {roster.members.length}
          </span>
        </div>

        {otherMembers.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
            No matches.
          </div>
        ) : (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherMembers.map((m) => (
              <MemberCard key={m.email} member={m} />
            ))}
          </section>
        )}
      </div>

      {profileUrl ? (
        <ProfileModal url={profileUrl} onClose={() => setProfileUrl(null)} />
      ) : null}
    </main>
  );
}

interface HeroProps {
  me: MeResponse;
  currentUser: RosterMember | null;
  onEditProfile: () => void;
  busy: boolean;
  error: string | null;
}

function HeroCard({ me, currentUser, onEditProfile, busy, error }: HeroProps): JSX.Element {
  const displayName = currentUser?.name ?? me.name ?? me.email ?? 'You';
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-orange-100 text-xl font-semibold text-orange-700">
          {initials || '·'}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-gray-900 truncate">{displayName}</h1>
          {currentUser?.pronouns ? (
            <p className="text-sm text-gray-600">{currentUser.pronouns}</p>
          ) : null}
          {!currentUser ? (
            <p className="mt-1 text-sm text-gray-500">
              You don't have a NameDrop entry yet — record your name to get started.
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 sm:flex-col sm:items-stretch">
          {currentUser?.recording ? (
            <PlayButton
              recordingUrl={currentUser.recording}
              ariaLabel={`Hear ${displayName}'s name`}
              size="lg"
            />
          ) : null}
          <button
            type="button"
            onClick={onEditProfile}
            disabled={busy}
            className="inline-flex items-center justify-center rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700 disabled:opacity-60"
          >
            {busy ? 'Opening…' : currentUser ? 'Edit my profile' : 'Record my name'}
          </button>
        </div>
      </div>
      {error ? (
        <p className="mt-3 text-sm text-red-600 break-words">Profile link failed: {error}</p>
      ) : null}
    </section>
  );
}

function MemberCard({ member }: { member: RosterMember }): JSX.Element {
  const initials = member.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <article className="group rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-orange-300 hover:shadow">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600 group-hover:bg-orange-100 group-hover:text-orange-700">
          {initials || '·'}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-gray-900 truncate" title={member.name}>
            {member.name}
          </h3>
          {member.pronouns ? (
            <p className="text-xs text-gray-600">{member.pronouns}</p>
          ) : null}
        </div>
        {member.recording ? (
          <PlayButton recordingUrl={member.recording} ariaLabel={`Hear ${member.name}'s name`} />
        ) : (
          <span className="text-[11px] text-gray-400 whitespace-nowrap">No audio</span>
        )}
      </div>
    </article>
  );
}

function PlayButton({
  recordingUrl,
  ariaLabel,
  size = 'md',
}: {
  recordingUrl: string;
  ariaLabel: string;
  size?: 'md' | 'lg';
}): JSX.Element {
  const dim = size === 'lg' ? 'h-12 w-12 text-lg' : 'h-9 w-9';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={() => {
        const a = new Audio(recordingUrl);
        void a.play().catch(() => {
          /* swallow autoplay errors — surfaced via UA */
        });
      }}
      className={`inline-flex ${dim} shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700 hover:bg-orange-200`}
    >
      ▶
    </button>
  );
}

function ProfileModal({ url, onClose }: { url: string; onClose: () => void }): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit profile"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-6xl h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
          <span className="text-sm font-medium text-gray-700">Edit my profile</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <iframe
          src={url}
          title="Edit profile"
          className="flex-1 w-full border-0"
          allow="microphone; camera; autoplay"
        />
      </div>
    </div>
  );
}
